import { useState, useRef, useEffect, useCallback } from "react";
import { Device } from "types/device.types";
import { useRemoveDeviceLink } from "./useDevices";
import { useDeviceUpdateManager } from "./useDeviceUpdateManager";
import { notify } from "utils/notify";

interface UseDevicesListActionsArgs {
  refetch: () => void;
}

export function useDevicesListActions({ refetch }: UseDevicesListActionsArgs) {
  const [wizardVisible, setWizardVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [locationSearch, setLocationSearch] = useState<{
    latitude: number | null;
    longitude: number | null;
    radiusKm: number;
    active: boolean;
  }>({
    latitude: null,
    longitude: null,
    radiusKm: 20,
    active: false,
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateManager = useDeviceUpdateManager(
    (message) => notify.success(message),
    (message) => notify.error(message),
  );

  const removeDeviceMutation = useRemoveDeviceLink();

  const debouncedSearch = useCallback((value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value);
      setCurrentPage(1);
    }, 500);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleLocationSearch = useCallback(
    (latitude: number, longitude: number, radiusKm: number) => {
      setLocationSearch({ latitude, longitude, radiusKm, active: true });
      setCurrentPage(1);
      notify.info(
        `Searching for devices within ${radiusKm}km of the selected location`,
      );
    },
    [],
  );

  const resetLocationSearch = useCallback(() => {
    setLocationSearch({
      latitude: null,
      longitude: null,
      radiusKm: 20,
      active: false,
    });
    notify.info("Showing all devices without location filter");
  }, []);

  const handleLocationSearchAndRefetch = useCallback(
    (lat: number, lng: number, _radiusKm: number) => {
      handleLocationSearch(lat, lng, 50);
      refetch();
    },
    [handleLocationSearch, refetch],
  );

  const handleClearLocationSearchAndRefetch = useCallback(() => {
    resetLocationSearch();
    refetch();
  }, [resetLocationSearch, refetch]);

  const handleAddDeviceClick = () => setWizardVisible(true);
  const handleWizardHide = () => setWizardVisible(false);

  const handleWizardComplete = (deviceName: string | undefined) => {
    if (deviceName) {
      notify.success(
        `Device ${deviceName} was successfully added and configured.`,
      );
    }
    setWizardVisible(false);
    refetch();
  };

  const onPageChange = (event: {
    first: number;
    rows: number;
    page: number;
  }) => {
    setCurrentPage(event.page + 1);
    setPageSize(event.rows);
  };

  const onSortChange = (field: string, order: string) => {
    setSortField(field);
    setSortOrder(order);
  };

  const handleDeleteDevice = (device: Device) => {
    setDeviceToDelete(device);
    setDeleteConfirmVisible(true);
  };

  const hideDeleteConfirm = () => {
    setDeleteConfirmVisible(false);
    setDeviceToDelete(null);
  };

  const executeDelete = async () => {
    if (
      !deviceToDelete ||
      deviceToDelete.accessType === "temporary" ||
      deviceToDelete.linkId === null ||
      deviceToDelete.linkId === undefined
    ) {
      hideDeleteConfirm();
      return;
    }

    try {
      await removeDeviceMutation.mutateAsync(String(deviceToDelete.linkId));
      notify.success("Device access removed from your account.");
      refetch();
      hideDeleteConfirm();
    } catch (error) {
      console.error("Error removing device access:", error);
      notify.error("Failed to remove device access. Please try again later.");
    }
  };

  const handleAliasUpdated = (_linkId?: number, _newAlias?: string) => {
    refetch();
  };

  return {
    wizardVisible,
    deleteConfirmVisible,
    deviceToDelete,
    searchValue,
    searchInputValue,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    locationSearch,
    setCurrentPage,
    updateManager,
    removeDeviceMutation,
    handleSearchChange,
    handleLocationSearchAndRefetch,
    handleClearLocationSearchAndRefetch,
    handleAddDeviceClick,
    handleWizardHide,
    handleWizardComplete,
    onPageChange,
    onSortChange,
    handleDeleteDevice,
    hideDeleteConfirm,
    executeDelete,
    handleAliasUpdated,
  };
}
